import type { AxiosInstance } from 'axios';
import axios from 'axios';

import type { ChatConfig, Message } from '../types';
import { generateUUID } from '../utils';

/**
 * Axios-based messaging service for Salesforce chat
 * Replaces EventSource with controlled HTTP polling for better reliability
 */
export class AxiosMessagingService {
  private axiosInstance: AxiosInstance;

  private config: ChatConfig;

  private jwt: string | null = null;

  private conversationId: string | null = null;

  private pollingInterval: NodeJS.Timeout | null = null;

  private isPolling = false;

  private maxRetries = 3;

  private retryCount = 0;

  constructor(config: ChatConfig) {
    this.config = config;
    this.axiosInstance = axios.create({
      baseURL: config.messagingUrl,
      timeout: 10000, // 10 second timeout
    });
  }

  /**
   * Set JWT token for authentication
   */
  setJWT(jwt: string): void {
    this.jwt = jwt;
    this.axiosInstance.defaults.headers.common.Authorization = `Bearer ${jwt}`;
  }

  /**
   * Set conversation ID for requests
   */
  setConversationId(conversationId: string): void {
    this.conversationId = conversationId;
    this.axiosInstance.defaults.headers.common['X-Conversation-ID'] =
      conversationId;
  }

  /**
   * Test connection to the Salesforce messaging endpoint
   */
  async testConnection(): Promise<{ success: boolean; error?: string }> {
    if (!this.jwt) {
      return { success: false, error: 'No JWT token available' };
    }

    try {
      // eslint-disable-next-line no-console
      console.log('🧪 Testing Salesforce messaging endpoint...');

      // Try to make a simple request to test connectivity
      const response = await this.axiosInstance.get('/eventrouter/v1/sse', {
        headers: {
          Authorization: `Bearer ${this.jwt}`,
          'X-Org-ID': this.config.organizationId,
          'X-Conversation-ID': this.conversationId || 'test',
        },
        timeout: 5000,
      });

      // eslint-disable-next-line no-console
      console.log('✅ Connection test successful:', response.status);
      return { success: true };
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          status?: number;
          statusText?: string;
          data?: unknown;
        };
        message?: string;
      };
      const errorMessage = axiosError.response?.status
        ? `HTTP ${axiosError.response.status}: ${axiosError.response.statusText}`
        : axiosError.message || 'Unknown error';

      console.error('❌ Connection test failed:', {
        error: errorMessage,
        status: axiosError.response?.status,
        data: axiosError.response?.data,
      });

      return { success: false, error: errorMessage };
    }
  }

  /**
   * Start polling for messages (replaces EventSource)
   */
  async startPolling(onMessage: (message: Message) => void): Promise<void> {
    if (this.isPolling) {
      // eslint-disable-next-line no-console
      console.log('📡 Already polling for messages');
      return;
    }

    if (!this.jwt || !this.conversationId) {
      throw new Error('JWT and conversation ID required for polling');
    }

    // eslint-disable-next-line no-console
    console.log('📡 Starting message polling...');
    this.isPolling = true;
    this.retryCount = 0;

    // Test connection first
    const connectionTest = await this.testConnection();
    if (!connectionTest.success) {
      console.error(
        '❌ Cannot start polling - connection test failed:',
        connectionTest.error
      );
      this.isPolling = false;
      return;
    }

    // Start polling loop
    this.pollForMessages(onMessage);
  }

  /**
   * Stop polling for messages
   */
  stopPolling(): void {
    // eslint-disable-next-line no-console
    console.log('🛑 Stopping message polling');
    this.isPolling = false;
    if (this.pollingInterval) {
      clearTimeout(this.pollingInterval);
      this.pollingInterval = null;
    }
    this.retryCount = 0;
  }

  /**
   * Poll for new messages using HTTP requests
   */
  private async pollForMessages(
    onMessage: (message: Message) => void
  ): Promise<void> {
    if (!this.isPolling) {
      return;
    }

    try {
      // For now, we'll just log that we're polling
      // In a real implementation, this would call a Salesforce API to get new messages
      // eslint-disable-next-line no-console
      console.log('🔄 Polling for messages...');

      // Reset retry count on successful poll
      this.retryCount = 0;

      // Schedule next poll
      this.pollingInterval = setTimeout(() => {
        this.pollForMessages(onMessage);
      }, 5000); // Poll every 5 seconds
    } catch (error: unknown) {
      console.error('❌ Error polling for messages:', error);

      this.retryCount++;
      if (this.retryCount >= this.maxRetries) {
        console.error(
          `🚫 Max polling retries reached (${this.maxRetries}), stopping`
        );
        this.stopPolling();
        return;
      }

      // Retry with exponential backoff
      const retryDelay = Math.min(1000 * 2 ** this.retryCount, 30000);
      // eslint-disable-next-line no-console
      console.log(
        `🔄 Retrying poll in ${retryDelay}ms (attempt ${this.retryCount}/${this.maxRetries})`
      );

      this.pollingInterval = setTimeout(() => {
        this.pollForMessages(onMessage);
      }, retryDelay);
    }
  }

  /**
   * Send a message using axios
   */
  async sendMessage(conversationId: string, content: string): Promise<Message> {
    if (!this.jwt) {
      throw new Error('JWT required for sending messages');
    }

    try {
      // eslint-disable-next-line no-console
      console.log('📤 Sending message via axios...');

      const messageId = generateUUID();

      const response = await this.axiosInstance.post(
        `/iamessage/api/v2/conversation/${conversationId}/message`,
        {
          message: {
            id: messageId,
            messageType: 'StaticContentMessage',
            staticContent: {
              formatType: 'Text',
              text: content,
            },
          },
          esDeveloperName: this.config.deploymentDeveloperName,
        },
        {
          headers: {
            Authorization: `Bearer ${this.jwt}`,
            'X-Org-ID': this.config.organizationId,
            'Content-Type': 'application/json',
          },
        }
      );

      // eslint-disable-next-line no-console
      console.log('✅ Message sent successfully:', response.status);

      // Return the message with the actual ID used
      return {
        id: messageId,
        conversationId,
        content,
        sender: 'customer',
        timestamp: new Date(),
        messageType: 'text',
        deliveryStatus: 'delivered',
      };
    } catch (error: unknown) {
      console.error('❌ Failed to send message:', error);
      throw error;
    }
  }
}
